import { browserStorage, type RecoveryStorage } from './storage.ts';
import { watchDraftConfirmation } from './draft-events.ts';
type Saved<T> = { version: 1; baseline: string; value: T; savedAt: string };
export function createDraft<T>(key: string, baseline: string, initial: T, valid: (value: unknown) => value is T, storage: RecoveryStorage = browserStorage) {
  let state = { value: initial, saved: false, stale: false, error: '', savedAt: '', changedElsewhere: false, unreadable: false };
  let last: string | null = null;
  let previous: Saved<T> | null = null;
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach(fn => fn());
  try {
    last = storage.getItem(key);
    if (last !== null) {
      const saved = JSON.parse(last) as Saved<T>;
      if (saved.version !== 1 || typeof saved.baseline !== 'string' || typeof saved.savedAt !== 'string' || !valid(saved.value)) throw new Error('Invalid draft');
      previous = saved;
      state = { ...state, saved: true, stale: saved.baseline !== baseline, savedAt: saved.savedAt, value: saved.baseline === baseline ? saved.value : initial };
    }
  } catch { state.unreadable = true; state.error = 'This browser draft could not be read. Keep the page open until browser storage is available.'; }
  const persist = (value: T) => {
    if (!valid(value)) return false;
    try {
      if (storage.getItem(key) !== last) {
        state = { ...state, changedElsewhere: true, error: 'This draft changed in another tab. Reload to read it before editing.' }; notify(); return false;
      }
      const saved: Saved<T> = { version: 1, baseline, value, savedAt: new Date().toISOString() };
      const encoded = JSON.stringify(saved);
      if (encoded.length > 2000000) throw new Error('Draft too large');
      storage.setItem(key, encoded);
      if (storage.getItem(key) !== encoded) throw new Error('Draft was not saved');
      last = encoded; previous = saved;
      state = { ...state, value, saved: true, stale: false, error: '', savedAt: saved.savedAt, changedElsewhere: false, unreadable: false }; notify(); return true;
    } catch {
      // Keep the latest entries in memory, but never label them as saved.
      state = { ...state, value, saved: false, error: 'These entries could not be saved in this browser. Keep this page open and restore browser storage.' }; notify(); return false;
    }
  };
  return {
    snapshot: () => state,
    reference: () => last === null ? undefined : { key, snapshot: last },
    subscribe: (fn: () => void) => {
      listeners.add(fn);
      const stop = watchDraftConfirmation((confirmedKey, snapshot) => {
        if (confirmedKey !== key || last !== snapshot) return;
        last = null; previous = null;
        state = { value: initial, saved: false, stale: false, error: '', savedAt: '', changedElsewhere: false, unreadable: false }; notify();
      });
      return () => { listeners.delete(fn); stop(); };
    },
    set: (change: T | ((current: T) => T)) => {
      if (state.stale || state.changedElsewhere || state.unreadable) return;
      persist(typeof change === 'function' ? (change as (current: T) => T)(state.value) : change);
    },
    useSaved: () => { if (previous && state.stale) persist(previous.value); },
    clear: () => {
      try {
        const current = storage.getItem(key);
        if (current !== null && current !== last) throw new Error('Another tab changed this draft. Reload before discarding.');
        storage.removeItem(key);
        if (storage.getItem(key) !== null) throw new Error('Draft was not cleared');
        last = null; previous = null;
        state = { value: initial, saved: false, stale: false, error: '', savedAt: '', changedElsewhere: false, unreadable: false }; notify();
      } catch { state = { ...state, error: 'The draft could not be cleared. Reload to review browser storage.' }; notify(); }
    },
    retrySave: () => { if (!state.stale && !state.changedElsewhere && !state.unreadable) persist(state.value); },
  };
}
export function stringFields<T extends Record<string, string>>(initial: T) {
  return (value: unknown): value is T => !!value && typeof value === 'object' && !Array.isArray(value)
    && Object.keys(initial).every(key => typeof (value as Record<string, unknown>)[key] === 'string' && ((value as T)[key].length <= 10000));
}
